import { Box, Flex, Grid, GridItem, Skeleton, Stack, VStack } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { Layout } from "../../../common/layout/Layout";
import { CONTENT_WIDTH } from "../../../config";

export const OrganizationProfileSkeleton = ({ embedded = false }: { embedded?: boolean }) => {
  const Wrapper = embedded ? EmbeddedWrapper : Layout;
  return (
    <Wrapper>
      <Grid
        templateAreas={`
          "top top"
          "sidebar content"
        `}
        templateColumns="auto 1fr"
        width="full"
      >
        {/* Top area skeleton with banner */}
        <GridItem
          area="top"
          display="flex"
          justifyContent="center"
          backgroundColor="smBackground"
          colSpan={2}
          borderBottom="1px solid"
          borderColor="smBorder"
        >
          <Flex justifyContent="space-between" width="full" maxWidth={CONTENT_WIDTH} px="2" py="6">
            {/* Organization banner skeleton - matching Banner.tsx layout */}
            <Flex width="full">
              {/* Logo placeholder matching the 120x120px with border in Banner */}
              <Skeleton
                width="120px"
                height="120px"
                borderRadius="5px"
                border="1px solid"
                borderColor="smBorder"
                display={{ base: "none", md: "block" }}
              />
              <Flex flex={1} alignItems="center" ml="20px">
                <Flex flexDirection="column" flex={1}>
                  {/* Organization name */}
                  <Skeleton height="30px" width="300px" mb={2} />
                  {/* Country with map marker */}
                  <Flex>
                    <Skeleton height="16px" width="150px" />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            {/* Edit button placeholder */}
            <Skeleton height="32px" width="100px" alignSelf="flex-start" />
          </Flex>
        </GridItem>

        {/* Sidebar skeleton - matching OrgProfileSidebar.tsx */}
        <GridItem
          area="sidebar"
          height="full"
          borderRight="1px solid"
          borderColor="smBorder"
          minW="240px"
          position="relative"
          px={4}
          py={12}
        >
          {/* Collapse button placeholder */}
          <Skeleton height="24px" width="24px" borderRadius="full" position="absolute" right="-4" top="2.5" />

          {/* Menu items - exactly matching the 4 items in OrgProfileSidebar */}
          <VStack spacing="2" align="stretch" width="full">
            {/* Details */}
            <Skeleton height="48px" width="100%" borderRadius="lg" />

            {/* Contracts */}
            <Skeleton height="48px" width="100%" borderRadius="lg" />

            {/* Sourcing Events */}
            <Skeleton height="48px" width="100%" borderRadius="lg" />

            {/* Reviews & Risk */}
            <Skeleton height="48px" width="100%" borderRadius="lg" />
          </VStack>
        </GridItem>

        {/* Content area skeleton - matching the sections layout */}
        <GridItem
          area="content"
          backgroundColor="smBackgroundSecondary"
          overflowY="auto"
          minHeight="calc(100vh - 218px)"
          p={6}
        >
          {/* Two-column layout matching InformationSection.tsx */}
          <Grid width="full" maxWidth="full" overflow="hidden" gridTemplateColumns="1fr 1fr" columnGap={6} rowGap={2}>
            {/* Left column */}
            <GridItem display="flex" flexDirection="column" gap="6">
              {/* Details card */}
              <Box backgroundColor="smBackground" borderRadius="lg" p={6} border="1px solid" borderColor="smBorder">
                <Skeleton height="24px" width="120px" mb={4} />
                <Stack spacing={6}>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <Skeleton height="20px" width="90%" />
                    <Skeleton height="20px" width="90%" />
                    <Skeleton height="20px" width="90%" />
                    <Skeleton height="20px" width="90%" />
                    <Skeleton height="20px" width="90%" />
                    <Skeleton height="20px" width="90%" />
                  </Grid>
                </Stack>
              </Box>

              {/* Linked Entries card */}
              <Box backgroundColor="smBackground" borderRadius="lg" p={6} border="1px solid" borderColor="smBorder">
                <Skeleton height="24px" width="140px" mb={4} />
                <Stack spacing={4}>
                  {Array(2)
                    .fill(0)
                    .map((_, i) => (
                      <Flex key={i} p={3} border="1px solid" borderColor="smBorder" borderRadius="md">
                        <Skeleton height="24px" width="70%" />
                      </Flex>
                    ))}
                </Stack>
              </Box>
            </GridItem>

            {/* Right column */}
            <GridItem display="flex" flexDirection="column" gap="6">
              {/* About Us card */}
              <Box backgroundColor="smBackground" borderRadius="lg" p={6} border="1px solid" borderColor="smBorder">
                <Skeleton height="24px" width="100px" mb={4} />
                <Stack spacing={3}>
                  <Skeleton height="16px" width="100%" />
                  <Skeleton height="16px" width="100%" />
                  <Skeleton height="16px" width="90%" />
                </Stack>
              </Box>

              {/* Specialties card */}
              <Box backgroundColor="smBackground" borderRadius="lg" p={6} border="1px solid" borderColor="smBorder">
                <Skeleton height="24px" width="120px" mb={4} />
                <Flex flexWrap="wrap" gap={2}>
                  {Array(5)
                    .fill(0)
                    .map((_, i) => (
                      <Skeleton key={i} height="30px" width="80px" borderRadius="full" />
                    ))}
                </Flex>
              </Box>

              {/* Contact Persons card */}
              <Box backgroundColor="smBackground" borderRadius="lg" p={6} border="1px solid" borderColor="smBorder">
                <Skeleton height="24px" width="150px" mb={4} />
                <Box border="1px solid" borderColor="smBorder" borderRadius="lg" p={4}>
                  <Stack spacing={3}>
                    {/* Table header */}
                    <Flex>
                      <Skeleton height="20px" width="20%" mr={2} />
                      <Skeleton height="20px" width="25%" mr={2} />
                      <Skeleton height="20px" width="20%" mr={2} />
                      <Skeleton height="20px" width="15%" mr={2} />
                      <Skeleton height="20px" width="15%" />
                    </Flex>
                    {/* Table rows */}
                    {Array(2)
                      .fill(0)
                      .map((_, i) => (
                        <Flex key={i}>
                          <Skeleton height="20px" width="20%" mr={2} />
                          <Skeleton height="20px" width="25%" mr={2} />
                          <Skeleton height="20px" width="20%" mr={2} />
                          <Skeleton height="20px" width="15%" mr={2} />
                          <Skeleton height="20px" width="15%" />
                        </Flex>
                      ))}
                  </Stack>
                </Box>
              </Box>
            </GridItem>
          </Grid>
        </GridItem>
      </Grid>
    </Wrapper>
  );
};

const EmbeddedWrapper = ({ children }: { children: ReactNode }) => <>{children}</>;
